import { ReactNode } from 'react';

export enum TFilterFieldType {
    SingleSelect = 'SingleSelect',
    MultiSelect = 'MultiSelect'
}

export interface IFilterFieldBase {
    id: string;
    label: string;
    type: TFilterFieldType;
    icon?: ReactNode;
}

export interface IFilterOption {
    label: string;
    value: string;
    color?: string;
}

export interface ISingleSelectField extends IFilterFieldBase {
    type: TFilterFieldType.SingleSelect;
    options: IFilterOption[];
}

export interface IMultiSelectField extends IFilterFieldBase {
    type: TFilterFieldType.MultiSelect;
    options: IFilterOption[];
}

export type TFilterField = ISingleSelectField | IMultiSelectField;

export type TFilterValue =
    | IFilterOption
    | null // For SingleSelect
    | IFilterOption[]; // For MultiSelect

export interface IFilter {
    id: string;
    value: TFilterValue;
    /** The field definition this filter instance is bound to. */
    field: TFilterField;
    /** True when the filter has a non-empty value (non-null for SingleSelect, non-empty array for MultiSelect). */
    isActive: boolean;
    /** Marks a freshly added filter so the UI can auto-open its popover; cleared on the next interaction. */
    isNew?: boolean;
}

export interface IInitialFilter {
    id: string;
    value: TFilterValue;
}

/** Localization overrides for filter UI labels. */
export interface IFilterTexts {
    /** Label for the "clear all filters" action. */
    removeAllFilters?: string;
    /** Label for the "add filter" trigger button. */
    addFilter?: string;
    /** Placeholder shown in the field selection menu. */
    selectFilter?: string;
    /** Placeholder shown in the SingleSelect option list. */
    selectOption?: string;
    /** Placeholder shown in the MultiSelect option list. */
    selectOptions?: string;
    /** Label for the action that clears the current filter's selection. */
    clearSelection?: string;
    /** Label for the action that removes the filter chip entirely. */
    deleteFilter?: string;
}

export interface IFilterCallbacks {
    /** Fires after any filter mutation with the full updated filter list. */
    onChange?: (filters: IInitialFilter[]) => void;
    /** Fires when a new filter chip is added (before a value is selected). */
    onAddFilter?: (filter: IInitialFilter) => void;
    /** Fires when a filter chip is removed by the user. */
    onDeleteFilter?: (filter: IInitialFilter) => void;
    /** Fires once when a filter transitions from inactive to active (first value selected). */
    onFilterActivated?: (filter: IInitialFilter) => void;
    /** Fires when the user clicks the "remove all filters" action. */
    onRemoveAll?: () => void;
}

export interface IFilterBaseProps {
    /** All possible filter fields the user can choose from; fields already active are excluded from the "add filter" menu. */
    availableFields: TFilterField[];
    /** Localization overrides for filter UI labels. */
    texts?: IFilterTexts;
}

export interface IFiltersProps extends IFilterBaseProps, IFilterCallbacks {
    /** Initially applied filters */
    initialFilters: IInitialFilter[];
}
